import { LineAlertCircle } from '@qr-front/icons/line-alert-circle'
import { LineBriefcase } from '@qr-front/icons/line-briefcase'
import { LineHome } from '@qr-front/icons/line-home'
import { LinePhone } from '@qr-front/icons/line-phone'
import { LinePrinter } from '@qr-front/icons/line-printer'
import { LineStar } from '@qr-front/icons/line-star'

import { TSvgIcon, TTranslationFunction } from '@/types'

export const CUSTOM_LABEL_MAX_LENGTH = 24

export const PHONE_TYPES = [
	'work',
	'mobile',
	'home',
	'workFax',
	'other',
	'custom',
] as const

export const EMAIL_TYPES = ['work', 'home', 'other', 'custom'] as const

export type TPhoneType = (typeof PHONE_TYPES)[number]
export type TEmailType = (typeof EMAIL_TYPES)[number]
export type TContactType = TPhoneType | TEmailType

export const DEFAULT_CONTACT_TYPE: TContactType = 'work'
export const CUSTOM_FALLBACK_TYPE: TContactType = 'other'

export type TContactEntry = {
	value: string
	type: TContactType
	label?: string
}

export type TContactField = string | (string | TContactEntry)[] | undefined

export type TContactTypeOption = {
	type: TContactType
	Icon: TSvgIcon
	labelKey: string
}

const OPTION_ICON: Record<TContactType, TSvgIcon> = {
	work: LineBriefcase,
	mobile: LinePhone,
	home: LineHome,
	workFax: LinePrinter,
	other: LineAlertCircle,
	custom: LineStar,
}

export const contactTypeLabelKey = (type: TContactType) =>
	`generator__content_form_section__about__contact_section__type__${type}`

const toOption = (type: TContactType): TContactTypeOption => ({
	type,
	Icon: OPTION_ICON[type],
	labelKey: contactTypeLabelKey(type),
})

export const PHONE_TYPE_OPTIONS: TContactTypeOption[] =
	PHONE_TYPES.map(toOption)

export const EMAIL_TYPE_OPTIONS: TContactTypeOption[] =
	EMAIL_TYPES.map(toOption)

export const createEntry = (
	type: TContactType = DEFAULT_CONTACT_TYPE,
): TContactEntry => ({ value: '', type })

export const capCustomLabel = (label: string) =>
	label.slice(0, CUSTOM_LABEL_MAX_LENGTH)

const isEntryObject = (
	item: unknown,
): item is { value: unknown; type?: unknown; label?: unknown } =>
	typeof item === 'object' && item !== null && 'value' in item

const toItemList = (raw: TContactField): unknown[] => {
	if (raw === undefined || raw === null || raw === '') return []
	return Array.isArray(raw) ? raw : [raw]
}

export const normalizeContactEntries = (
	raw: TContactField,
	fallbackType: TContactType,
	allowedTypes: readonly TContactType[] = PHONE_TYPES,
): TContactEntry[] =>
	toItemList(raw).reduce<TContactEntry[]>((entries, item) => {
		if (typeof item === 'string') {
			entries.push({ value: item, type: fallbackType })
			return entries
		}

		if (!isEntryObject(item) || typeof item.value !== 'string')
			return entries

		const type = allowedTypes.includes(item.type as TContactType)
			? (item.type as TContactType)
			: fallbackType

		if (type === 'custom' && typeof item.label === 'string') {
			entries.push({
				value: item.value,
				type,
				label: capCustomLabel(item.label),
			})
			return entries
		}

		entries.push({ value: item.value, type })
		return entries
	}, [])

export const serializeContactEntries = (
	entries: TContactEntry[],
): TContactEntry[] =>
	entries.reduce<TContactEntry[]>((serialized, entry) => {
		if (!entry.value.length) return serialized

		if (entry.type !== 'custom') {
			serialized.push({ value: entry.value, type: entry.type })
			return serialized
		}

		const label = capCustomLabel(entry.label?.trim() ?? '')

		serialized.push(
			label.length
				? { value: entry.value, type: entry.type, label }
				: { value: entry.value, type: CUSTOM_FALLBACK_TYPE },
		)
		return serialized
	}, [])

const withAtLeastOneRow = (entries: TContactEntry[]): TContactEntry[] =>
	entries.length ? entries : [createEntry()]

export const normalizePhoneEntries = (
	raw: TContactField,
	alternativePhoneNumber?: string,
): TContactEntry[] => {
	const entries = normalizeContactEntries(
		raw,
		DEFAULT_CONTACT_TYPE,
		PHONE_TYPES,
	)

	if (alternativePhoneNumber) {
		entries.push({
			value: alternativePhoneNumber,
			type: DEFAULT_CONTACT_TYPE,
		})
	}

	return withAtLeastOneRow(entries)
}

export const normalizeEmailEntries = (raw: TContactField): TContactEntry[] =>
	withAtLeastOneRow(
		normalizeContactEntries(raw, DEFAULT_CONTACT_TYPE, EMAIL_TYPES),
	)

export type TLabeledContactEntry = {
	value: string
	label: string
}

export const resolveEntryType = (entry: TContactEntry): TContactType =>
	entry.type === 'custom' && !entry.label?.trim()
		? CUSTOM_FALLBACK_TYPE
		: entry.type

export const toLabeledEntries = (
	entries: TContactEntry[],
	t: TTranslationFunction,
): TLabeledContactEntry[] =>
	entries
		.filter((entry) => entry.value.length)
		.map((entry) => {
			const type = resolveEntryType(entry)

			return {
				value: entry.value,
				label:
					type === 'custom'
						? capCustomLabel(entry.label!.trim())
						: t(contactTypeLabelKey(type)),
			}
		})
